import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Layout } from './components/layout';
import { LoginPage } from './components/auth';
import { DashboardPage, CentralDashboardPage, ActivitiesFilteredPage } from './components/dashboard';
import { ProjectsListPage, ProjectDetailPage, ProjectForm, ProjectsFilteredPage, ProjectsTablePage } from './components/projects';
import {
  WorkPackageDetailPage,
  WorkPackageForm,
} from './components/workpackages';
import { LoadingOverlay } from './components/common';
import {
  UsersPage,
  GroupsPage,
  StatusActivitiesPage,
  StatusProjectsPage,
  ActivityTypesPage,
  StatusesPage,
  PrioritiesPage,
  ProcurementDocTypesPage,
  DeliverableDocTypesPage,
  DeliverableTypesPage,
  ImplementationTypesPage,
  ImplementationDocTypesPage,
  ContractTypesPage,
  ListaInvestitiiAdminPage,
} from './components/admin';
import { SettingsPage } from './components/settings';
import { CautarePage } from './components/search';
import { ListaInvestitiiPage } from './components/investitii';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingOverlay message="Se verifică sesiunea..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin Route wrapper - doar pentru administratori
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingOverlay message="Se verifică sesiunea..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.admin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acces interzis</h2>
          <p className="text-gray-600 mb-4">Nu aveți permisiunea de a accesa această pagină.</p>
          <p className="text-sm text-gray-500">Doar administratorii pot accesa această secțiune.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Public Route wrapper (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingOverlay message="Se verifică sesiunea..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - Activitățile mele */}
        <Route index element={<DashboardPage />} />

        {/* Panou central */}
        <Route path="panou-central" element={<CentralDashboardPage />} />

        {/* Lista de investiții — anexa la buget. `ProtectedRoute` e suficient:
            lista e publică pentru orice cont, iar legătura spre proiect o taie
            backendul, nu ruta. */}
        <Route path="lista-investitii" element={<ListaInvestitiiPage />} />

        {/* Activități filtrate */}
        <Route path="activitati/de-facut" element={<ActivitiesFilteredPage filter="todo" />} />
        <Route path="activitati/in-lucru" element={<ActivitiesFilteredPage filter="in-progress" />} />
        <Route path="activitati/finalizate" element={<ActivitiesFilteredPage filter="completed" />} />

        {/* Proiecte filtrate */}
        <Route path="proiecte/toate" element={<ProjectsTablePage />} />
        <Route path="proiecte/coordonate" element={<ProjectsFilteredPage filter="coordinated" />} />
        <Route path="proiecte/in-lucru" element={<ProjectsFilteredPage filter="involved" />} />
        <Route path="proiecte/finalizate" element={<ProjectsFilteredPage filter="finished" />} />

        {/* Căutare în dosarele investițiilor. `ProtectedRoute` e suficient:
            accesul se filtrează pe proiect, în backend, nu pe rol. */}
        <Route path="cautare" element={<CautarePage />} />

        {/* Proiecte */}
        <Route path="projects" element={<ProjectsListPage />} />
        <Route path="projects/new" element={<ProjectForm />} />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="projects/:projectId/edit" element={<ProjectForm />} />
        <Route
          path="projects/:projectId/work-packages/new"
          element={<WorkPackageForm />}
        />

        {/* Work Packages */}
        <Route
          path="work-packages/:workPackageId"
          element={<WorkPackageDetailPage />}
        />
        <Route
          path="work-packages/:workPackageId/edit"
          element={<WorkPackageForm />}
        />

        {/* Settings */}
        <Route path="settings" element={<SettingsPage />} />

        {/* Admin */}
        <Route path="admin/utilizatori" element={<AdminRoute><UsersPage /></AdminRoute>} />
        <Route path="admin/grupuri" element={<AdminRoute><GroupsPage /></AdminRoute>} />
        <Route path="admin/lista-investitii" element={<AdminRoute><ListaInvestitiiAdminPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/status-activitati" element={<AdminRoute><StatusActivitiesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/status-proiecte" element={<AdminRoute><StatusProjectsPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/tip-activitate" element={<AdminRoute><ActivityTypesPage /></AdminRoute>} />

        {/* Nomenclatoare - Status și Prioritate */}
        <Route path="admin/nomenclatoare/status" element={<AdminRoute><StatusesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/prioritate" element={<AdminRoute><PrioritiesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/documente-achizitii" element={<AdminRoute><ProcurementDocTypesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/documente-livrabile" element={<AdminRoute><DeliverableDocTypesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/tipuri-livrabile" element={<AdminRoute><DeliverableTypesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/tipuri-contracte" element={<AdminRoute><ContractTypesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/tipuri-implementare" element={<AdminRoute><ImplementationTypesPage /></AdminRoute>} />
        <Route path="admin/nomenclatoare/documente-implementare" element={<AdminRoute><ImplementationDocTypesPage /></AdminRoute>} />
      </Route>

      {/* 404 - Redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
